import { OrganizationRoleType } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import {
  canUpdateOrgEntry,
  hasContractCreateAccess,
  hasContractViewAccess,
  hasProjectCreateAccess,
  hasProjectViewAccess,
  hasSourcingCreateAccess,
  hasSourcingViewAccess,
  isCreator,
} from "../auth/utils";
import { useViewAsRole } from "./viewAsRoleStore";

export const useSub = () => {
  const authState = useLoggedInWithOrgContextState();
  const viewAsRole = useViewAsRole();

  if (!authState) {
    throw Error("Auth state with org context not available");
  }

  const org = authState.selectedOrg;
  const { hasBuyerAccess, hasContractAccess, hasProjectAccess } = org.subscription;
  const isSupplier = !(hasBuyerAccess || hasContractAccess || hasProjectAccess);
  // The preview is a v2-only concept: outside /v2 (V1 pages, the wizards) the real roles always
  // apply. window.location rather than the router keeps useSub renderable without a Router
  // provider; ViewAsRoleScope clears the state on leaving /v2, this check guarantees the very
  // first render outside v2 is already unaffected.
  const previewInScope = window.location.pathname.startsWith("/v2");
  const previewedRole = !isSupplier && previewInScope && org.canEdit ? viewAsRole : null;

  const clampedByPreview = (grantedByBff: boolean, previewedRoleAllows: (roles: OrganizationRoleType[]) => boolean) =>
    grantedByBff && (previewedRole === null || previewedRoleAllows([previewedRole]));
  const unlessPreviewingARole = (grantedByBff: boolean) => grantedByBff && previewedRole === null;

  const viewsSourcingEvents = clampedByPreview(org.canViewSourcingEvents, hasSourcingViewAccess);
  const viewsContracts = clampedByPreview(org.canViewContracts, hasContractViewAccess);

  return {
    canUpdateOrganization: unlessPreviewingARole(org.canEdit),
    canEditDocumentTemplates: unlessPreviewingARole(org.canEditDocumentTemplates),
    canConfigureContracts: unlessPreviewingARole(org.canConfigureContracts),
    canCreateContractTemplates: unlessPreviewingARole(org.canCreateContractTemplate),
    canViewSourcingEvents: isSupplier || (hasBuyerAccess && viewsSourcingEvents),
    canViewContracts: hasContractAccess && viewsContracts,
    viewsContractsAsSupplier: isSupplier,
    canViewProjects: hasProjectAccess && clampedByPreview(org.canViewProjects, hasProjectViewAccess),
    canCreateProjects: clampedByPreview(org.canCreateProject, hasProjectCreateAccess),
    canReportNeeds: org.canReportNeed,
    canAssessNeeds: org.subscription.hasNeedsAccess && (isSupplier || viewsSourcingEvents),
    canUseContractSearch: org.subscription.hasFencyContractSearchAccess && hasContractAccess && viewsContracts,
    hasContractAccess: org.subscription.hasContractAccess,
    hasBuyerAccess: org.subscription.hasBuyerAccess,
    isSupplier,
    hasSignatureAccess: org.subscription.hasSignatureAccess,
    hasBankIdAccess: org.subscription.hasBankIdAccess,
    canCreateEvents: hasBuyerAccess && clampedByPreview(org.canCreateEvent, hasSourcingCreateAccess),
    canCreateContracts: clampedByPreview(org.canCreateContract, hasContractCreateAccess),
    canUpdateOrganizationEntries: clampedByPreview(org.canUpdateOrganizationEntries, canUpdateOrgEntry),
    canUpdateContactPersons: clampedByPreview(org.canUpdateContactPersons, canUpdateOrgEntry),
    canCreateOrganizationAssessments: clampedByPreview(org.canCreateOrganizationAssessments, isCreator),
    hasProductAccess: org.hasProductAccess,
    hasProjectAccess: org.hasProjectAccess,
    hasAiAccess: org.hasAiAccess,
    hasFencyContractSearchAccess: org.subscription.hasFencyContractSearchAccess,
    hasAssistantAccess: org.subscription.hasAssistantAccess,
    hasNeedsAccess: org.subscription.hasNeedsAccess,
    fencySyncEnabled: org.subscription.fencySyncEnabled,
    hasDocumentTemplateAccess: org.hasDocumentTemplateAccess,
    hasIFRSAccess: org.hasIFRSAccess,
    hasOrganizationAssessmentAccess: org.subscription.hasOrganizationAssessmentAccess,
    hasNegotiationAcces: org.subscription.hasNegotiationAccess,
  };
};

export type SubAccess = ReturnType<typeof useSub>;
